import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '../../utils/seo';

export default function ServicesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useSEO({
    title: '岡山市の屋根工事サービス | 雨漏り修理・葺き替え・塗装 | おかやま屋根工事',
    description: '岡山市で屋根工事のプロフェッショナルが対応。雨漏り修理、屋根葺き替え、屋根塗装、防水工事など幅広いサービスを提供。無料診断・見積もりで安心スタート。自社職人による高品質な施工を適正価格で。',
    keywords: '岡山市,屋根工事,サービス,雨漏り修理,屋根葺き替え,屋根塗装,防水工事,雨樋工事,屋根点検',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema('サービス内容', '岡山市で屋根工事のプロフェッショナルが対応。雨漏り修理、屋根葺き替え、屋根塗装、防水工事など幅広いサービスを提供。'),
        generateBreadcrumbSchema([
          { name: 'ホーム', url: '/' },
          { name: 'サービス内容', url: '/services' }
        ])
      ]
    }
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/04f146fe66e164e946a570b7d8d120d2.png"
            alt="岡山市 屋根工事 道具"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
          <span className="inline-block px-5 py-2 bg-white text-[#1e3a5f] text-sm font-semibold rounded-full mb-6">
            サービス
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            屋根のことなら<br />
            すべてお任せください
          </h1>
          <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto">
            雨漏り修理から新築まで、幅広く対応
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">提供サービス</h2>
            <p className="text-base sm:text-lg text-gray-600">岡山市の屋根工事のことなら、何でもご相談ください</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <ServiceCard
              icon="ri-drop-line"
              title="雨漏り修理"
              description="雨漏りの原因を徹底調査し、根本から解決。緊急対応も可能です。岡山市内なら最短即日対応いたします。"
              image="https://public.readdy.ai/ai/img_res/9b9c25f0dc1514efc8871e53ec16f9ec.jpg"
            />
            <ServiceCard
              icon="ri-home-gear-line"
              title="屋根葺き替え"
              description="古くなった屋根を新しく。瓦、スレート、金属屋根など、あらゆる屋根材に対応します。"
              image="https://readdy.ai/api/search-image?query=Japanese%20workers%20installing%20new%20roof%20tiles%20on%20residential%20house%20in%20Okayama%2C%20professional%20roofing%20installation%20work%20without%20any%20text%20or%20signage%2C%20showing%20quality%20craftsmanship&width=600&height=400&seq=service002&orientation=landscape"
            />
            <ServiceCard
              icon="ri-paint-brush-line"
              title="屋根塗装"
              description="屋根の美観を保ち、耐久性を向上。遮熱塗料や防水塗料など、用途に応じた塗料をご提案します。"
              image="https://readdy.ai/api/search-image?query=Japanese%20worker%20applying%20professional%20roof%20coating%20paint%20with%20spray%20equipment%2C%20clean%20painting%20work%20without%20any%20text%20or%20signage%2C%20showing%20professional%20technique&width=600&height=400&seq=service003&orientation=landscape"
            />
            <ServiceCard
              icon="ri-water-flash-line"
              title="雨樋工事"
              description="雨樋の詰まりや破損を修理。適切な排水で建物を守ります。定期メンテナンスもお任せください。"
              image="https://readdy.ai/api/search-image?query=Japanese%20worker%20repairing%20and%20cleaning%20rain%20gutter%20system%20on%20residential%20house%2C%20professional%20maintenance%20work%20without%20any%20text%20or%20signage%2C%20showing%20attention%20to%20detail&width=600&height=400&seq=service004&orientation=landscape"
            />
            <ServiceCard
              icon="ri-shield-check-line"
              title="防水工事"
              description="ベランダや陸屋根の防水工事。雨漏りを未然に防ぎ、建物の寿命を延ばします。"
              image="https://readdy.ai/api/search-image?query=Japanese%20worker%20applying%20waterproofing%20membrane%20on%20flat%20roof%20surface%2C%20professional%20waterproofing%20work%20without%20any%20text%20or%20signage%2C%20showing%20technical%20expertise&width=600&height=400&seq=service005&orientation=landscape"
            />
            <ServiceCard
              icon="ri-search-eye-line"
              title="屋根点検"
              description="定期的な点検で問題を早期発見。無料診断を実施中。ドローンを使った詳細調査も可能です。"
              image="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/928ccd877ece72e3e3a2780196b7428f.jpeg"
            />
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">施工の流れ</h2>
            <p className="text-base sm:text-lg text-gray-600">お問い合わせから完成まで、安心の5ステップ</p>
          </div>
          
          <div className="space-y-8 sm:space-y-12">
            <ProcessStep
              number="01"
              title="お問い合わせ"
              description="お電話またはお問い合わせフォームより、お気軽にご連絡ください。お客様のお悩みやご要望を丁寧にお伺いします。"
              image="https://readdy.ai/api/search-image?query=Japanese%20customer%20service%20representative%20smiling%20while%20talking%20on%20phone%20in%20professional%20office%2C%20friendly%20consultation%20scene%20without%20any%20text%20or%20signage&width=400&height=300&seq=process001&orientation=landscape"
            />
            <ProcessStep
              number="02"
              title="現地調査・診断"
              description="経験豊富な職人が現地にお伺いし、屋根の状態を詳しく調査。問題点を明確にし、最適な施工方法をご提案します。"
              image="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/61b514df6dae3f4da6b9f27c6e462f3a.jpeg"
            />
            <ProcessStep
              number="03"
              title="お見積もり提示"
              description="調査結果をもとに、詳細なお見積もりを作成。工事内容、使用材料、工期、費用を分かりやすくご説明します。"
              image="https://readdy.ai/api/search-image?query=Japanese%20contractor%20presenting%20detailed%20estimate%20document%20to%20homeowner%20at%20table%2C%20professional%20business%20meeting%20without%20any%20text%20or%20signage&width=400&height=300&seq=process003&orientation=landscape"
            />
            <ProcessStep
              number="04"
              title="施工開始"
              description="ご契約後、工事日程を調整し施工開始。自社職人が責任を持って丁寧に作業を進めます。工事中も随時ご報告いたします。"
              image="https://readdy.ai/api/search-image?query=Japanese%20roofing%20team%20working%20together%20on%20residential%20roof%20installation%2C%20coordinated%20professional%20work%20without%20any%20text%20or%20signage%2C%20showing%20teamwork%20and%20skill&width=400&height=300&seq=process004&orientation=landscape"
            />
            <ProcessStep
              number="05"
              title="完成・アフターフォロー"
              description="工事完了後、お客様と一緒に仕上がりを確認。保証書を発行し、定期的なアフターフォローで長期的にサポートします。"
              image="https://readdy.ai/api/search-image?query=Japanese%20homeowner%20and%20contractor%20shaking%20hands%20in%20front%20of%20completed%20house%20with%20beautiful%20new%20roof%2C%20satisfaction%20and%20trust%20without%20any%20text%20or%20signage&width=400&height=300&seq=process005&orientation=landscape"
            />
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">料金目安</h2>
            <p className="text-base sm:text-lg text-gray-600">※建物の状態や使用材料により変動します</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <PricingCard
              title="雨漏り修理"
              price="50,000円〜"
              features={[
                '原因調査',
                '部分修理',
                '防水処理',
                '1年保証'
              ]}
            />
            <PricingCard
              title="屋根工事"
              price="8,000円／1件"
              features={[
                '当日小修理をご依頼の場合は無料',
                '見積もりチェック',
                '口頭説明',
                '写真確認込み'
              ]}
              featured
            />
            <PricingCard
              title="屋根葺き替え"
              price="1,200,000円〜"
              features={[
                '既存屋根撤去',
                '下地補修',
                '新規屋根材施工',
                '10年保証'
              ]}
            />
          </div>
          
          <p className="text-center text-sm sm:text-base text-gray-500 mt-8 sm:mt-12">
            ※上記は一般的な30坪住宅の目安価格です。詳しくはお見積もりをご依頼ください。
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 sm:py-24 lg:py-32">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Beautiful%20Okayama%20city%20skyline%20with%20clear%20blue%20sky%20and%20traditional%20Japanese%20architecture%2C%20peaceful%20urban%20landscape%20without%20any%20text%20or%20signage%2C%20professional%20cityscape%20photography&width=1920&height=800&seq=servicecta001&orientation=landscape"
            alt="岡山市 街並み"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 sm:mb-8">
            無料見積もり・ご相談<br />
            今すぐお電話ください
          </h2>
          <p className="text-lg sm:text-xl text-white mb-8 sm:mb-12">
            営業時間 9:00〜18:00
          </p>
          
          <a
            href="tel:0860123456"
            className="inline-flex items-center justify-center gap-4 px-8 sm:px-12 py-5 sm:py-6 bg-[#ff6b35] rounded-2xl hover:bg-[#e55a28] transition-all duration-300 shadow-2xl cursor-pointer mb-6"
          >
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-phone-fill text-2xl sm:text-3xl text-white"></i>
            </div>
            <span className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white whitespace-nowrap">086-012-3456</span>
            <i className="ri-arrow-right-up-line text-2xl sm:text-3xl text-white"></i>
          </a>
          
          <p className="text-sm sm:text-base text-white/80">または</p>
          
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-8 py-4 mt-4 text-base sm:text-lg font-bold text-[#1e3a5f] bg-white rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
          >
            お問い合わせフォームへ
            <i className="ri-mail-line ml-2 text-xl"></i>
          </Link>
        </div>
      </section>
    </div>
  );
}

function ServiceCard({ icon, title, description, image }: { icon: string; title: string; description: string; image: string }) {
  return (
    <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">
      <div className="h-48 overflow-hidden">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
        />
      </div>
      <div className="p-6 sm:p-8">
        <div className="w-14 h-14 bg-[#1e3a5f]/10 rounded-full flex items-center justify-center mb-4">
          <i className={`${icon} text-2xl text-[#1e3a5f]`}></i>
        </div>
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">{title}</h3>
        <p className="text-gray-600 leading-relaxed mb-4 text-sm sm:text-base">{description}</p>
        <Link
          to="/contact"
          className="inline-flex items-center text-[#1e3a5f] font-semibold hover:underline cursor-pointer text-sm sm:text-base"
        >
          詳しく見る
          <i className="ri-arrow-right-line ml-1"></i>
        </Link>
      </div>
    </div>
  );
}

function ProcessStep({ number, title, description, image }: { number: string; title: string; description: string; image: string }) {
  return (
    <div className="flex flex-col md:flex-row gap-6 items-start">
      <div className="flex-shrink-0">
        <div className="w-20 h-20 bg-[#1e3a5f] rounded-full flex items-center justify-center">
          <span className="text-2xl font-bold text-white">{number}</span>
        </div>
      </div>
      <div className="flex-1">
        <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">{title}</h3>
        <p className="text-gray-600 leading-relaxed mb-4 text-sm sm:text-base">{description}</p>
      </div>
      <div className="w-full md:w-48 h-32 flex-shrink-0">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover object-top rounded-lg shadow-md"
        />
      </div>
    </div>
  );
}

function PricingCard({ title, price, features, featured = false }: { title: string; price: string; features: string[]; featured?: boolean }) {
  return (
    <div className={`rounded-xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-all duration-300 ${
      featured ? 'bg-[#1e3a5f] text-white ring-4 ring-[#ff6b35]' : 'bg-white'
    }`}>
      {featured && (
        <span className="inline-block px-3 py-1 bg-[#ff6b35] text-white text-xs font-bold rounded-full mb-4">
          人気No.1
        </span>
      )}
      <h3 className={`text-2xl sm:text-3xl font-bold mb-4 ${featured ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
      <div className="mb-6">
        <span className={`text-3xl sm:text-4xl font-bold ${featured ? 'text-white' : 'text-[#1e3a5f]'}`}>{price}</span>
      </div>
      <ul className="space-y-3 mb-8">
        {features.map((feature, index) => (
          <li key={index} className="flex items-center gap-3">
            <i className={`ri-check-line text-xl ${featured ? 'text-[#ff6b35]' : 'text-[#1e3a5f]'}`}></i>
            <span className={`text-sm sm:text-base ${featured ? 'text-white' : 'text-gray-600'}`}>{feature}</span>
          </li>
        ))}
      </ul>
      <Link
        to="/contact"
        className={`block w-full text-center px-6 py-3 rounded-lg font-bold transition-all duration-300 whitespace-nowrap cursor-pointer text-sm sm:text-base ${
          featured
            ? 'bg-[#ff6b35] text-white hover:bg-[#e55a28]'
            : 'bg-[#1e3a5f] text-white hover:bg-[#152a45]'
        }`}
      >
        見積もり依頼
      </Link>
    </div>
  );
}
